import * as React from "react";

import {
  makeStyles,
  OutlinedInputProps,
  TextField,
  Theme,
} from "@material-ui/core";
import MaskedInput from "react-text-mask";

import { Field, ValueOf } from "client/lib/apt-merchant";

interface InputFieldProps {
  field: Field<any>;
  hasError: boolean;
  handleChange: (field: Field<any>, value: ValueOf<typeof field>) => void;
  helperText?: string;
  inputMask?: InputMask;
}

const useStyles = makeStyles((theme: Theme) => ({
  helperText: {
    marginLeft: 0,
  },
  wrapper: {
    backgroundColor: theme.palette.common.white,
  },
}));

interface MaskedFieldCustomProps {
  inputMask?: InputMask;
  inputRef?: any;
}

type BSBMask = [RegExp, RegExp, RegExp, "-", RegExp, RegExp, RegExp];
type DoBMask = Array<RegExp | "-">;
type SortCodeMask = [RegExp, RegExp, "-", RegExp, RegExp, "-", RegExp, RegExp];

export type InputMask = BSBMask | DoBMask | SortCodeMask;

//This will be moved to uiFieldMap after merging refactor
const propertyNamesToMaskForPii = [
  // business numbers
  "abn",
  "companyNumber",
  "ein",
  // owner address
  "city",
  "postalCode",
  "state",
  "street",
  "street2",
  // owner
  "firstName",
  "lastName",
  "nameAndDob",
  "phone",
  "ssn",
  // customer support
  "customerSupportEmail",
  "customerSupportPhone",
  // bank
  "account",
  "account2",
  "bankAccountName",
  "bankAccountName2",
  "canadaChequingAccount",
  "confirmAccount",
  "confirmAccount2",
  "confirmCanadaChequingAccount",
  "signatoryName",
];

const MaskedFieldCustom: React.FC<MaskedFieldCustomProps> = ({
  inputMask,
  inputRef: _inputRef, // unused
  ...rest
}) => <MaskedInput guide={false} mask={inputMask} showMask {...rest} />;

export const InputField: React.FC<InputFieldProps> = ({
  field,
  hasError,
  handleChange,
  helperText,
  inputMask,
}) => {
  /**
   * MaterialUI inputs (and selects) will treat a component as "uncontroleled" if the first
   * value you pass it is undefined. If you then later pass it a new value that's defined,
   * an error is thrown that you shouldn't change a component from controlled to uncontrolled
   *
   * Because our merchant field values are initialized to undefined, and later populated after
   * api/restore call returns, this results in material input fields thinking they're uncontrolled.
   *
   * The way around that is to always give it a value. However, since we also don't update the
   * underlying merchant until blur, we need a way to be able to update the local component.
   *
   * Hence, this:
   */
  const [localStateFieldValue, setLocalStateFieldValue] = React.useState(
    field?.value ?? ""
  );

  // Programatically determine the input props necessary to avoid label overlap
  // with preloaded input value as described here: https://mui.com/components/text-fields/#limitations
  const inputLabelPropsObj =
    localStateFieldValue !== "" ? { shrink: true } : {};

  const classes = useStyles();

  React.useEffect(() => {
    setLocalStateFieldValue(field.value || "");
  }, [field.value]);

  const updateField = (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    if (e.currentTarget.value !== field.value) {
      handleChange(field, localStateFieldValue);
    }
  };

  const handleAutoformat = (value: string, field: Field<any>): string => {
    if (field.formatOnChange) {
      return field.formatOnChange(value);
    }
    return value;
  };

  const piiClassName = propertyNamesToMaskForPii.includes(field.propertyName)
    ? "fs-mask"
    : "";

  let inputProps: Partial<OutlinedInputProps> | undefined = {
    className: classes.wrapper,
  };

  if (inputMask) {
    inputProps = {
      ...inputProps,
      inputComponent: MaskedFieldCustom,
      inputProps: {
        inputMask,
      },
    };
  }

  return (
    <TextField
      className={`${piiClassName}`}
      error={hasError}
      fullWidth
      helperText={helperText}
      FormHelperTextProps={{
        classes: {
          root: classes.helperText,
        },
      }}
      id={field.propertyName}
      label={field.i18nValues.label}
      onBlur={(e) => updateField(e as React.FocusEvent<HTMLInputElement>)}
      onChange={(e) => {
        const formattedValue = handleAutoformat(e.currentTarget.value, field);
        setLocalStateFieldValue(formattedValue);
      }}
      onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          updateField(e);
        }
      }}
      placeholder={field.i18nValues.placeholder}
      type={field.uiFieldType}
      value={localStateFieldValue}
      variant="outlined"
      InputProps={inputProps}
      inputProps={{
        ...field.uiInputBaseProps,
        ["data-testid"]: field.propertyName,
      }}
      InputLabelProps={inputLabelPropsObj}
    />
  );
};
